// calculators/loan-consolidation/src/calculator.ts
// UI logic for the public Debt Consolidation Loan Calculator.
// Compares making credit-card minimum payments vs. consolidating into a fixed
// personal loan / HELOC. Renders net interest savings + a 2-series chart.

import {
  monthlyRateFromDailyCompounding,
  simulateMinimumPayoff,
  simulatePayoff,
} from "../../../../shared/payoffSimulation";
import {
  renderMultiSeriesChart,
  seriesFromMonths,
  clearChart,
  type ChartSeries,
} from "../../_shared/multiSeriesChart";

const CALC_ID = "loan_consolidation";
const CHART_ID = "tc-chart";

type FieldName = "balance" | "cardApr" | "loanApr" | "diyPayment";

interface Inputs {
  balance: number;
  cardApr: number; // decimal
  loanApr: number; // decimal
  termMonths: number;
  diyPayment: number;
}

interface ValidationResult {
  valid: boolean;
  values?: Inputs;
  errors: Partial<Record<FieldName, string>>;
}

const DEBOUNCE_MS = 300;
const GA_INPUT_DEBOUNCE_MS = 1000;
const MAX_AMOUNT = 200000;
const MAX_APR_PCT = 50;

function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

function t(
  key: string,
  fallback: string,
  params?: Record<string, string | number>
): string {
  const w =
    typeof window !== "undefined"
      ? (window as unknown as {
          i18next?: {
            t: (k: string, p?: Record<string, string | number>) => string;
          };
        })
      : undefined;
  if (w && w.i18next && typeof w.i18next.t === "function") {
    const val = w.i18next.t(key, { defaultValue: fallback, ...(params || {}) });
    return typeof val === "string" ? val : fallback;
  }
  if (params) {
    return fallback.replace(/\{\{(\w+)\}\}/g, (_, k) =>
      params[k] === undefined ? `{{${k}}}` : String(params[k])
    );
  }
  return fallback;
}

function gtag(...args: unknown[]): void {
  const w = window as unknown as { gtag?: (...a: unknown[]) => void };
  if (typeof w.gtag === "function") w.gtag(...args);
}

// Current site locale — same source/priority as js/i18n.js and store-tracking.js
// (i18next active language → <html lang> → ?lang= → 'en'). Sent on every event.
function getLang(): string {
  const SUPPORTED = ["en", "es", "fr", "en-GB", "en-AU", "en-CA", "fr-CA"];
  try {
    const w = window as unknown as {
      i18next?: { language?: string };
      getSiteLang?: () => string;
    };
    if (typeof w.getSiteLang === "function") return w.getSiteLang();
    if (w.i18next && typeof w.i18next.language === "string" && w.i18next.language) {
      return w.i18next.language;
    }
    const docLang = document.documentElement.getAttribute("lang");
    if (docLang && SUPPORTED.indexOf(docLang) !== -1) return docLang;
    const param = new URLSearchParams(window.location.search).get("lang");
    if (param && SUPPORTED.indexOf(param) !== -1) return param;
  } catch {
    /* fall through */
  }
  return "en";
}

function formatCurrency(n: number): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

function parseAmount(raw: string): number | null {
  const cleaned = raw.replace(/[\s,$]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function parsePct(raw: string): number | null {
  const cleaned = raw.replace(/[\s,%]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return n / 100;
}

function readRadio(name: string): string | null {
  const el = document.querySelector<HTMLInputElement>(
    `input[name="${name}"]:checked`
  );
  return el ? el.value : null;
}

// Tracks whether the user has manually edited the DIY payment field. Until they
// do, the field is smart-synced to the computed loan payment each recalc.
let diyManuallyEdited = false;

function readInputs(): Record<FieldName, string> {
  return {
    balance: ($("tc-input-balance") as HTMLInputElement | null)?.value || "",
    cardApr: ($("tc-input-cardApr") as HTMLInputElement | null)?.value || "",
    loanApr: ($("tc-input-loanApr") as HTMLInputElement | null)?.value || "",
    diyPayment: ($("tc-input-diyPayment") as HTMLInputElement | null)?.value || "",
  };
}

function validate(raw: Record<FieldName, string>): ValidationResult {
  const errors: Partial<Record<FieldName, string>> = {};

  const balance = parseAmount(raw.balance);
  if (balance === null || balance <= 0) {
    errors.balance = t(
      "calculators.loanConsolidation.error_balance_required",
      "Enter a total card balance greater than 0."
    );
  } else if (balance > MAX_AMOUNT) {
    errors.balance = t(
      "calculators.loanConsolidation.error_balance_too_large",
      "Balance must be $200,000 or less."
    );
  }

  const cardApr = parsePct(raw.cardApr);
  if (cardApr === null || cardApr <= 0) {
    errors.cardApr = t(
      "calculators.loanConsolidation.error_card_apr_required",
      "Enter your current card APR."
    );
  } else if (cardApr * 100 > MAX_APR_PCT) {
    errors.cardApr = t(
      "calculators.loanConsolidation.error_apr_too_large",
      "APR must be 50% or less."
    );
  }

  const loanApr = parsePct(raw.loanApr);
  if (loanApr === null || loanApr < 0) {
    errors.loanApr = t(
      "calculators.loanConsolidation.error_loan_apr_required",
      "Enter the loan or HELOC APR."
    );
  } else if (loanApr * 100 > MAX_APR_PCT) {
    errors.loanApr = t(
      "calculators.loanConsolidation.error_apr_too_large",
      "APR must be 50% or less."
    );
  }

  const termMonths = Number(readRadio("loanTerm") || "36");

  // DIY payment is validated only when the user has manually edited it. Until
  // then the field smart-syncs to the computed loan payment (compute() fills
  // it in), so an empty / unedited field is not an error.
  let diyPayment = 0;
  if (diyManuallyEdited) {
    const parsed = parseAmount(raw.diyPayment);
    if (balance !== null && balance > 0) {
      const floor = Math.max(25, 0.02 * balance);
      if (parsed === null || parsed < floor) {
        errors.diyPayment = t(
          "calculators.loanConsolidation.error_diy_too_small",
          "Enter at least {{min}} — below that the balance never goes down.",
          { min: formatCurrency(floor) }
        );
      } else if (parsed > balance) {
        errors.diyPayment = t(
          "calculators.loanConsolidation.error_diy_too_large",
          "Payment can't exceed your balance."
        );
      } else {
        diyPayment = parsed;
      }
    } else if (parsed !== null) {
      diyPayment = parsed;
    }
  }

  if (
    Object.keys(errors).length === 0 &&
    balance !== null &&
    cardApr !== null &&
    loanApr !== null
  ) {
    return {
      valid: true,
      values: { balance, cardApr, loanApr, termMonths, diyPayment },
      errors,
    };
  }
  return { valid: false, errors };
}

function renderFieldError(field: FieldName, message: string | null): void {
  const errEl = $(`tc-error-${field}`);
  const inputEl = $(`tc-input-${field}`);
  if (errEl) {
    errEl.textContent = message || "";
    errEl.style.display = message ? "" : "none";
  }
  if (inputEl) {
    if (message) inputEl.setAttribute("aria-invalid", "true");
    else inputEl.removeAttribute("aria-invalid");
  }
}

function showResults(): void {
  const r = $("tc-results");
  if (r) r.style.display = "block";
}

function hideResults(): void {
  const r = $("tc-results");
  if (r) r.style.display = "none";
  clearChart(CHART_ID);
}

interface ComputeResult {
  loanMonthly: number;
  loanTotalInterest: number;
  loanMonths: number | null;
  cardsTotalInterest: number;
  cardsMonths: number | null;
  cardsNeverPaysOff: boolean;
  netSavings: number;
  diyTotalInterest: number;
  diyMonths: number | null;
  diyNeverPaysOff: boolean;
  savingsVsDiy: number;
  cards: ChartSeries;
  loan: ChartSeries;
  diy: ChartSeries;
}

function compute(v: Inputs): ComputeResult {
  const { balance: B, cardApr, loanApr, termMonths: T } = v;

  const r = monthlyRateFromDailyCompounding(loanApr);
  const loanMonthly = r > 0 ? (B * r) / (1 - Math.pow(1 + r, -T)) : B / T;

  // Loan payoff at a fixed monthly payment over the term.
  const loanSim = simulatePayoff({
    balance: B,
    apr: loanApr,
    monthlyPayment: loanMonthly,
    months: T,
    stopOnPaidOff: true,
  });

  // Cards on shrinking minimum payments.
  const cardSim = simulateMinimumPayoff({ balance: B, apr: cardApr });
  const cardsNeverPaysOff = cardSim.monthsToPayoff === null;

  const netSavings = cardSim.totalInterest - loanSim.totalInterest;

  // "Same payment, on your card": the loan's monthly payment applied to the
  // card balance at the CARD's (higher) APR. When the field hasn't been
  // manually edited, diyPayment is 0 here — fall back to the loan payment so
  // the default scenario is "same payment, card rate".
  const diyPayment = v.diyPayment > 0 ? v.diyPayment : loanMonthly;
  const diySim = simulatePayoff({
    balance: B,
    apr: cardApr,
    monthlyPayment: diyPayment,
    months: 1200,
    stopOnPaidOff: true,
  });
  const diyNeverPaysOff = diySim.monthsToPayoff === null;

  const savingsVsDiy = diySim.totalInterest - loanSim.totalInterest;

  // ── Chart series ─────────────────────────────────────────────────────────
  // Cap the card series for the visual; loan ends at T.
  const cap = Math.min(
    Math.max(T, cardSim.monthsToPayoff ?? 0, diySim.monthsToPayoff ?? 0, 120),
    cardSim.months.length
  );
  const cardMonths = cardSim.months.slice(0, cap);
  const diyMonthsLog = diySim.months.slice(0, cap);

  const cards: ChartSeries = {
    label: t("calculators.loanConsolidation.series_cards", "Card minimums"),
    color: "#94A3B8",
    points: seriesFromMonths(cardMonths),
    stillOwing: cardsNeverPaysOff,
  };
  const loan: ChartSeries = {
    label: t("calculators.loanConsolidation.series_loan", "Consolidation loan"),
    color: "#00876B",
    points: seriesFromMonths(loanSim.months),
  };
  const diy: ChartSeries = {
    label: t(
      "calculators.loanConsolidation.series_diy",
      "Same payment, on your card"
    ),
    color: "#F59E0B",
    points: seriesFromMonths(diyMonthsLog),
    stillOwing: diyNeverPaysOff,
  };

  return {
    loanMonthly,
    loanTotalInterest: loanSim.totalInterest,
    loanMonths: loanSim.monthsToPayoff,
    cardsTotalInterest: cardSim.totalInterest,
    cardsMonths: cardSim.monthsToPayoff,
    cardsNeverPaysOff,
    netSavings,
    diyTotalInterest: diySim.totalInterest,
    diyMonths: diySim.monthsToPayoff,
    diyNeverPaysOff,
    savingsVsDiy,
    cards,
    loan,
    diy,
  };
}

function monthsToYears(months: number | null): string {
  if (months === null) return t("calculators.loanConsolidation.never", "never");
  if (months < 12) return `${months} ${t("calculators.loanConsolidation.months", "mo")}`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem === 0
    ? `${years} ${t("calculators.loanConsolidation.years", "yr")}`
    : `${years} ${t("calculators.loanConsolidation.years", "yr")} ${rem} ${t("calculators.loanConsolidation.months", "mo")}`;
}

function renderResults(v: Inputs, r: ComputeResult): void {
  const positive = r.netSavings >= 0;

  // Smart-default sync: until the user manually edits the DIY field, keep it
  // populated with the computed loan payment. Direct `.value =` assignment does
  // NOT fire an `input` event, so this won't flip `diyManuallyEdited`.
  if (!diyManuallyEdited) {
    const diyInput = $("tc-input-diyPayment") as HTMLInputElement | null;
    if (diyInput) diyInput.value = r.loanMonthly.toFixed(2);
  }
  const resetEl = $("tc-diy-reset");
  if (resetEl) resetEl.style.display = diyManuallyEdited ? "" : "none";

  const heroEl = $("tc-result-hero");
  if (heroEl) {
    if (r.cardsNeverPaysOff) {
      // netSavings is derived from the 1200-month cap and is meaningless when
      // the cards never pay off. Lead with the qualitative benefit instead.
      heroEl.classList.remove("is-negative");
      heroEl.innerHTML = t(
        "calculators.loanConsolidation.neverPaysOffQualitative",
        "Card minimums never fully pay off this balance — a consolidation loan does"
      );
    } else {
      heroEl.classList.toggle("is-negative", !positive);
      const amt = `<strong>${formatCurrency(Math.abs(r.netSavings))}</strong>`;
      heroEl.innerHTML = positive
        ? t(
            "calculators.loanConsolidation.result_saves",
            "Consolidating saves you {{amount}} in interest",
            { amount: amt }
          )
        : t(
            "calculators.loanConsolidation.result_costs",
            "Consolidating costs you {{amount}} more in interest",
            { amount: amt }
          );
    }
  }

  const monthlyEl = $("tc-result-monthly");
  if (monthlyEl) {
    monthlyEl.innerHTML =
      t("calculators.loanConsolidation.metric_loan_payment", "Loan payment") +
      `: <strong>${formatCurrency(r.loanMonthly)}</strong>/mo`;
  }

  const cardIntEl = $("tc-result-card-interest");
  if (cardIntEl) {
    cardIntEl.innerHTML =
      t("calculators.loanConsolidation.metric_card_interest", "Card interest") +
      `: <strong>${r.cardsNeverPaysOff ? "∞" : formatCurrency(r.cardsTotalInterest)}</strong>`;
  }

  const loanIntEl = $("tc-result-loan-interest");
  if (loanIntEl) {
    loanIntEl.innerHTML =
      t("calculators.loanConsolidation.metric_loan_interest", "Loan interest") +
      `: <strong>${formatCurrency(r.loanTotalInterest)}</strong>`;
  }

  const diyIntEl = $("tc-result-diy-interest");
  if (diyIntEl) {
    diyIntEl.innerHTML =
      t(
        "calculators.loanConsolidation.metric_diy_interest",
        "Same-payment interest"
      ) +
      `: <strong>${r.diyNeverPaysOff ? "∞" : formatCurrency(r.diyTotalInterest)}</strong>`;
  }

  // Line 1 — card-minimums outcome.
  const timesEl = $("tc-result-times");
  if (timesEl) {
    timesEl.innerHTML = r.cardsNeverPaysOff
      ? t(
          "calculators.loanConsolidation.card_outcome_never",
          "Card minimums never pay this balance off."
        )
      : t(
          "calculators.loanConsolidation.card_outcome_payoff",
          "Card minimums pay off in {{cardTime}}.",
          { cardTime: monthsToYears(r.cardsMonths) }
        );
  }

  // Line 2 — loan-saves framing vs. the same payment on the card.
  const rateSavingsEl = $("tc-result-rate-savings");
  if (rateSavingsEl) {
    const amount = `<strong>${formatCurrency(Math.abs(r.savingsVsDiy))}</strong>`;
    const monthDelta =
      r.diyMonths !== null && r.loanMonths !== null
        ? r.diyMonths - r.loanMonths
        : null;
    const pluralMonths = (n: number): string =>
      n === 1
        ? t("calculators.loanConsolidation.month_one", "{{n}} month", { n })
        : t("calculators.loanConsolidation.month_other", "{{n}} months", { n });

    if (r.diyNeverPaysOff) {
      rateSavingsEl.innerHTML = t(
        "calculators.loanConsolidation.same_pay_never",
        "The same payment on your card never pays off within the loan's {{loanTime}} term.",
        { loanTime: monthsToYears(r.loanMonths) }
      );
    } else if (r.savingsVsDiy >= 0) {
      // Loan wins.
      rateSavingsEl.innerHTML =
        monthDelta !== null && monthDelta >= 1
          ? t(
              "calculators.loanConsolidation.loan_saves_money_time",
              "The loan saves {{amount}} and {{months}} versus paying the same on your card.",
              { amount, months: pluralMonths(monthDelta) }
            )
          : t(
              "calculators.loanConsolidation.loan_saves_money",
              "The loan saves {{amount}} versus paying the same on your card.",
              { amount }
            );
    } else {
      // Loan loses (card rate <= loan rate).
      rateSavingsEl.innerHTML =
        monthDelta !== null && monthDelta < 0
          ? t(
              "calculators.loanConsolidation.same_pay_costs_less_faster",
              "Paying the same on your card actually costs {{amount}} less and pays off {{months}} sooner than the loan.",
              { amount, months: pluralMonths(Math.abs(monthDelta)) }
            )
          : t(
              "calculators.loanConsolidation.same_pay_costs_less",
              "Paying the same on your card actually costs {{amount}} less than the loan.",
              { amount }
            );
    }
  }

  const svg = $(CHART_ID) as unknown as SVGSVGElement | null;
  if (svg)
    renderMultiSeriesChart(
      svg,
      [r.cards, r.diy, r.loan],
      t("calculators.loanConsolidation.stillOwing", "still owing")
    );

  const banner = $("tc-warn-banner");
  if (banner) {
    banner.style.display = positive ? "none" : "block";
    if (!positive) {
      const bodyEl = $("tc-warn-body");
      if (bodyEl)
        bodyEl.innerHTML = t(
          "calculators.loanConsolidation.warn_body",
          "At this loan rate, consolidating actually costs {{amount}} more in interest than your cards. A lower loan APR — or a shorter term — may flip the result.",
          { amount: `<strong>${formatCurrency(Math.abs(r.netSavings))}</strong>` }
        );
    }
  }

  gtag("event", "calc_result_shown", {
    calculator: CALC_ID,
    placement: "calculator",
    term_months: v.termMonths,
    net_positive: positive ? "yes" : "no",
    cards_never_pays_off: r.cardsNeverPaysOff ? "yes" : "no",
    lang: getLang(),
  });
}

let recalcTimer: number | null = null;
let gaInputTimer: number | null = null;
let lastChangedField: string | null = null;

function run(): void {
  const raw = readInputs();
  const v = validate(raw);

  (["balance", "cardApr", "loanApr", "diyPayment"] as FieldName[]).forEach((f) =>
    renderFieldError(f, v.errors[f] || null)
  );

  if (!v.valid || !v.values) {
    hideResults();
    return;
  }

  try {
    const result = compute(v.values);
    showResults();
    renderResults(v.values, result);
  } catch (err) {
    const msg =
      err instanceof Error ? err.message.slice(0, 80) : String(err).slice(0, 80);
    hideResults();
    const errBox = $("tc-runtime-error");
    if (errBox) {
      errBox.textContent = t(
        "calculators.loanConsolidation.runtime_error",
        "Something went wrong. Try refreshing the page."
      );
      errBox.style.display = "block";
    }
    gtag("event", "calc_error", { calculator: CALC_ID, placement: "calculator", message: msg, lang: getLang() });
  }
}

function scheduleRecalc(): void {
  if (recalcTimer !== null) window.clearTimeout(recalcTimer);
  recalcTimer = window.setTimeout(() => {
    recalcTimer = null;
    run();
  }, DEBOUNCE_MS);
}

function scheduleGaInputEvent(): void {
  if (gaInputTimer !== null) window.clearTimeout(gaInputTimer);
  gaInputTimer = window.setTimeout(() => {
    gaInputTimer = null;
    if (lastChangedField) {
      gtag("event", "calc_input_change", {
        calculator: CALC_ID,
        placement: "calculator",
        field: lastChangedField,
        lang: getLang(),
      });
    }
  }, GA_INPUT_DEBOUNCE_MS);
}

export function attachCalculator(): void {
  (["balance", "cardApr", "loanApr", "diyPayment"] as FieldName[]).forEach(
    (field) => {
      const el = $(`tc-input-${field}`) as HTMLInputElement | null;
      if (!el) return;
      el.addEventListener("input", () => {
        // A real user `input` on the DIY field locks it to the user's value;
        // the smart-default sync stops overwriting it from here on.
        if (field === "diyPayment") diyManuallyEdited = true;
        lastChangedField = field;
        const errBox = $("tc-runtime-error");
        if (errBox) errBox.style.display = "none";
        scheduleRecalc();
        scheduleGaInputEvent();
      });
    }
  );

  // "Match loan payment" reset: clears the manual-edit flag, repopulates with
  // the current loan payment, and recalcs. Shown only while manually edited.
  const diyReset = $("tc-diy-reset") as HTMLElement | null;
  if (diyReset) {
    diyReset.addEventListener("click", (e) => {
      e.preventDefault();
      diyManuallyEdited = false;
      renderFieldError("diyPayment", null);
      lastChangedField = "diyPayment";
      run();
    });
  }

  document
    .querySelectorAll<HTMLInputElement>('input[name="loanTerm"]')
    .forEach((el) => {
      el.addEventListener("change", () => {
        lastChangedField = "loanTerm";
        scheduleRecalc();
        scheduleGaInputEvent();
      });
    });

  document.querySelectorAll<HTMLAnchorElement>("[data-calc-cta]").forEach((a) => {
    a.addEventListener("click", () => {
      const store = a.getAttribute("data-calc-cta") || "unknown";
      const lang = getLang();
      // Keep calc_cta_click (it has GA history). The unified
      // `app_store_click` event is now owned solely by /js/store-tracking.js
      // (the global delegated listener) to avoid double-counting.
      gtag("event", "calc_cta_click", { calculator: CALC_ID, placement: "calculator", store, lang });
    });
  });

  const calcButton = $("tc-calculate-button") as HTMLButtonElement | null;
  if (calcButton) {
    calcButton.addEventListener("click", (e) => {
      e.preventDefault();
      run();
    });
  }

  const form = $("tc-form") as HTMLFormElement | null;
  if (form) {
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      run();
    });
  }

  gtag("event", "calc_view", { calculator: CALC_ID, placement: "calculator", lang: getLang() });

  const raw = readInputs();
  if (raw.balance || raw.loanApr) run();
}

// Test-only export. `compute` is otherwise private to the module; calculator
// tests import it to validate the DIY scenario math (totalInterest, savingsVsDiy,
// diyMonths/never-pays-off) without a DOM. Not used at runtime by the page.
export { compute as __computeForTests };
export type { Inputs as __InputsForTests };
